// components/CustomCursor.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import RotatingText from './RotatingText';
import { useMobileDetection } from '../hooks/useMobileDetection';
import { useCursor } from '../context/CursorContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';

const CustomCursor = () => {
    // TUTTI I HOOKS DEVONO ESSERE CHIAMATI PRIMA DI QUALSIASI EARLY RETURN
    const { isMobile } = useMobileDetection();
    const location = useLocation();
    const { isHoveringComputerScreen, isHoveringLamp, isLampOn, isHoveringReel } = useCursor();
    const { language } = useLanguage();
    const t = useTranslation(language);

    // All hooks first - before any conditional logic
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [visible, setVisible] = useState(false);
    const [cursorType, setCursorType] = useState('default');
    const [cursorText, setCursorText] = useState('');
    const [isHovering, setIsHovering] = useState(false);
    const [isHoveringLink, setIsHoveringLink] = useState(false);

    // Location-based state calculations
    const isAboutPage = location.pathname === '/about';
    const isWorksPage = location.pathname === '/works';
    const isWorkDetailPage = location.pathname.includes('/work/');
    const isCodePage = location.pathname === '/code';
    const isCodeDetailPage = location.pathname.includes('/code/') && location.pathname !== '/code';
    const isPlaygroundPage = location.pathname === '/playground';
    const isDarkPage = isAboutPage || isWorksPage || isWorkDetailPage || isCodePage || isCodeDetailPage || isPlaygroundPage;

    // Playground OS state
    const [playgroundOS, setPlaygroundOS] = useState('mac');
    const [isLoadingMac, setIsLoadingMac] = useState(false);

    // Reset cursor state when location changes
    useEffect(() => {
        if (isMobile) return;

        // Reset cursor state to prevent double cursor
        setCursorType('default');
        setCursorText('');
        setIsHovering(false);
        setIsHoveringLink(false);
        setIsLoadingMac(false);

        // Clean up any lingering cursor styles
        const allElements = document.querySelectorAll('*');
        allElements.forEach(el => {
            if (el.style.cursor === 'auto' || el.style.cursor === 'none') {
                el.style.cursor = '';
            }
        });
    }, [location.pathname, isMobile]);

    // Track mouse position
    useEffect(() => {
        // Skip mouse tracking on mobile
        if (isMobile) return;

        const updatePosition = (e) => {
            setPosition({ x: e.clientX, y: e.clientY });
        };

        document.addEventListener('mousemove', updatePosition);
        setVisible(true);

        return () => document.removeEventListener('mousemove', updatePosition);
    }, [isMobile]);

    // Track Playground OS changes
    useEffect(() => {
        if (!isPlaygroundPage) return;

        const checkPlaygroundOS = () => {
            const os = document.body.getAttribute('data-playground-os') || 'mac';
            setPlaygroundOS(os);
        };

        // Initial check
        checkPlaygroundOS();

        // Watch for changes
        const observer = new MutationObserver(checkPlaygroundOS);
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-playground-os'] });

        return () => observer.disconnect();
    }, [isPlaygroundPage]);

    // Track playground interactive elements (window drag, buttons, links)
    useEffect(() => {
        if (!isPlaygroundPage || isMobile) return;

        const handleDragEnter = () => {
            if (isLoadingMac) return;
            setCursorType(playgroundOS === 'linux' ? 'linuxGrab' : playgroundOS === 'windows' ? 'windowsGrab' : 'macGrab');
        };

        const handleDragLeave = () => {
            if (isLoadingMac) return;
            setCursorType(playgroundOS === 'linux' ? 'linuxPointer' : playgroundOS === 'windows' ? 'windowsPointer' : 'macPointer');
        };

        const handleButtonEnter = () => {
            if (isLoadingMac) return;
            setCursorType(playgroundOS === 'linux' ? 'linuxHover' : playgroundOS === 'windows' ? 'windowsHover' : 'macHover');
        };

        const handleButtonLeave = () => {
            if (isLoadingMac) return;
            setCursorType(playgroundOS === 'linux' ? 'linuxPointer' : playgroundOS === 'windows' ? 'windowsPointer' : 'macPointer');
        };

        const timeoutId = setTimeout(() => {
            // Window headers (draggable)
            const windowHeaders = document.querySelectorAll('.window-header');
            windowHeaders.forEach(header => {
                header.addEventListener('mouseenter', handleDragEnter);
                header.addEventListener('mouseleave', handleDragLeave);
            });

            // Buttons and links
            const interactiveElements = document.querySelectorAll('.playground-page button, .playground-page a, .playground-page [role="button"]');
            interactiveElements.forEach(el => {
                el.addEventListener('mouseenter', handleButtonEnter);
                el.addEventListener('mouseleave', handleButtonLeave);
            });

            // Dock icons - trigger spinner on macOS
            if (playgroundOS === 'mac') {
                const dockIcons = document.querySelectorAll('.dock-icon, [class*="DockIcon"]');
                dockIcons.forEach(icon => {
                    const handleDockClick = () => {
                        setIsLoadingMac(true);
                        setCursorType('macSpinner');
                        setTimeout(() => {
                            setIsLoadingMac(false);
                            setCursorType('macPointer');
                        }, 1000);
                    };
                    icon.addEventListener('click', handleDockClick);
                });
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const windowHeaders = document.querySelectorAll('.window-header');
            windowHeaders.forEach(header => {
                header.removeEventListener('mouseenter', handleDragEnter);
                header.removeEventListener('mouseleave', handleDragLeave);
            });

            const interactiveElements = document.querySelectorAll('.playground-page button, .playground-page a, .playground-page [role="button"]');
            interactiveElements.forEach(el => {
                el.removeEventListener('mouseenter', handleButtonEnter);
                el.removeEventListener('mouseleave', handleButtonLeave);
            });

            if (playgroundOS === 'mac') {
                const dockIcons = document.querySelectorAll('.dock-icon, [class*="DockIcon"]');
                dockIcons.forEach(icon => {
                    icon.removeEventListener('click', () => { });
                });
            }
        };
    }, [isPlaygroundPage, playgroundOS, isMobile, isLoadingMac]);

    // Detect current section or page
    useEffect(() => {
        // Skip section detection on mobile
        if (isMobile) return;

        // Check for playground page - set cursor based on OS
        if (isPlaygroundPage) {
            if (isLoadingMac && playgroundOS === 'mac') {
                setCursorType('macSpinner');
            } else if (playgroundOS === 'linux') {
                setCursorType('linuxPointer');
            } else if (playgroundOS === 'windows') {
                setCursorType('windowsPointer');
            } else {
                setCursorType('macPointer');
            }
            setCursorText('');
            return;
        }

        // Check for about or works page
        if (isDarkPage) {
            // Don't override cursor if we're hovering on a project
            if (!isHovering) {
                setCursorType('white');
                setCursorText('');
            }
            return;
        }

        const detectSection = () => {
            // Get all sections
            const heroSection = document.querySelector('.hero-section');
            const aboutSection = document.getElementById('about');
            const worksSection = document.getElementById('works');
            const skillsSection = document.getElementById('skills');
            const contactSection = document.getElementById('contact');
            const codeShowcaseSection = document.querySelector('.code-showcase-section');

            // Get current scroll position
            const viewportHeight = window.innerHeight;

            // Check which elements are in view
            const isElementInView = (el) => {
                if (!el) return false;
                const rect = el.getBoundingClientRect();
                return (
                    rect.top <= viewportHeight / 2 &&
                    rect.bottom >= viewportHeight / 2
                );
            };

            // Explicitly check contact section first
            if (contactSection && isElementInView(contactSection)) {
                setCursorType('contact');
                setCursorText(''); // No default text, only on hover
                return;
            }

            // Check code showcase section
            if (codeShowcaseSection && isElementInView(codeShowcaseSection)) {
                if (!isHovering) {
                    setCursorType('dark');
                    setCursorText('');
                }
                return;
            }

            // Then check other sections in reverse order (bottom to top)
            if (skillsSection && isElementInView(skillsSection)) {
                setCursorType('dark');
                setCursorText('');
                return;
            }

            if (worksSection && isElementInView(worksSection)) {
                setCursorType(isHovering ? 'work' : 'dark');
                setCursorText(isHovering ? t('cursor_click_see_more') : '');
                return;
            }

            if (aboutSection && isElementInView(aboutSection)) {
                setCursorType('dark');
                setCursorText('');
                return;
            }

            if (!isHovering) {
                setCursorType('hero');
                setCursorText(t('cursor_scroll_explore'));
            }
        };

        // Watch for section changes on scroll
        window.addEventListener('scroll', detectSection);
        detectSection(); // Initial detection

        return () => window.removeEventListener('scroll', detectSection);
    }, [isHovering, isDarkPage, isMobile, isPlaygroundPage, playgroundOS, isLoadingMac, language]);

    // Add project card hover detection
    useEffect(() => {
        // Skip hover detection on mobile or if not on works/work detail page
        if (isMobile || (!isWorksPage && !isWorkDetailPage)) return;

        const handleMouseEnter = () => {
            setIsHovering(true);
            setCursorType('work');
            setCursorText(t('cursor_view_work'));
        };

        const handleMouseLeave = () => {
            setIsHovering(false);
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const projectCards = document.querySelectorAll('.project-content-area');

            projectCards.forEach(card => {
                card.addEventListener('mouseenter', handleMouseEnter);
                card.addEventListener('mouseleave', handleMouseLeave);
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const projectCards = document.querySelectorAll('.project-content-area');
            projectCards.forEach(card => {
                card.removeEventListener('mouseenter', handleMouseEnter);
                card.removeEventListener('mouseleave', handleMouseLeave);
            });
        };
    }, [location.pathname, isMobile, isWorksPage, isWorkDetailPage, language]);

    // Add terminal hover detection for Code and Playground pages
    useEffect(() => {
        // Skip on mobile or if not on code/playground page
        if (isMobile || (!isCodePage && !isPlaygroundPage)) return;

        const handleTerminalEnter = () => {
            setCursorType('terminal');
            setCursorText(t('cursor_execute_script'));
        };

        const handleTerminalLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        const terminalElements = document.querySelectorAll('.terminal-interactive');

        terminalElements.forEach(terminal => {
            terminal.addEventListener('mouseenter', handleTerminalEnter);
            terminal.addEventListener('mouseleave', handleTerminalLeave);
        });

        return () => {
            terminalElements.forEach(terminal => {
                terminal.removeEventListener('mouseenter', handleTerminalEnter);
                terminal.removeEventListener('mouseleave', handleTerminalLeave);
            });
        };
    }, [isMobile, isCodePage, isPlaygroundPage, language]);

    // Add code project item hover detection for Code page
    useEffect(() => {
        // Skip on mobile or if not on code page
        if (isMobile || !isCodePage) return;

        const handleProjectEnter = () => {
            setIsHovering(true);
            setCursorType('codeProject');
            setCursorText(t('cursor_view_project'));
        };

        const handleProjectLeave = () => {
            setIsHovering(false);
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const projectRows = document.querySelectorAll('.code-project-row');

            projectRows.forEach(row => {
                row.addEventListener('mouseenter', handleProjectEnter);
                row.addEventListener('mouseleave', handleProjectLeave);
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const projectRows = document.querySelectorAll('.code-project-row');
            projectRows.forEach(row => {
                row.removeEventListener('mouseenter', handleProjectEnter);
                row.removeEventListener('mouseleave', handleProjectLeave);
            });
        };
    }, [isMobile, isCodePage, language]);

    // Add playground link hover detection for Code page
    useEffect(() => {
        // Skip on mobile or if not on code page
        if (isMobile || !isCodePage) return;

        const handlePlaygroundEnter = () => {
            setCursorType('playgroundLink');
            setCursorText(t('cursor_go_to_playground'));
        };

        const handlePlaygroundLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        const playgroundLink = document.querySelector('.playground-link-area');

        if (playgroundLink) {
            playgroundLink.addEventListener('mouseenter', handlePlaygroundEnter);
            playgroundLink.addEventListener('mouseleave', handlePlaygroundLeave);
        }

        return () => {
            if (playgroundLink) {
                playgroundLink.removeEventListener('mouseenter', handlePlaygroundEnter);
                playgroundLink.removeEventListener('mouseleave', handlePlaygroundLeave);
            }
        };
    }, [isMobile, isCodePage, language]);

    // Add GitHub and Medium link hover detection for Code page
    useEffect(() => {
        // Skip on mobile or if not on code page
        if (isMobile || !isCodePage) return;

        const handleSocialLinkEnter = () => {
            setCursorType('socialLink');
            setCursorText(t('cursor_click_navigate'));
        };

        const handleSocialLinkLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const socialLinks = document.querySelectorAll('.code-social-link');

            socialLinks.forEach(link => {
                link.addEventListener('mouseenter', handleSocialLinkEnter);
                link.addEventListener('mouseleave', handleSocialLinkLeave);
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const socialLinks = document.querySelectorAll('.code-social-link');
            socialLinks.forEach(link => {
                link.removeEventListener('mouseenter', handleSocialLinkEnter);
                link.removeEventListener('mouseleave', handleSocialLinkLeave);
            });
        };
    }, [isMobile, isCodePage, language]);

    // Add link hover detection for CodeDetail page
    useEffect(() => {
        // Skip on mobile or if not on code detail page
        if (isMobile || !isCodeDetailPage) return;

        const handleLinkEnter = (e) => {
            // Don't override if it's the next project (has specific class)
            if (e.currentTarget.closest('.code-detail-next-project')) {
                return;
            }
            setIsHoveringLink(true);
            setCursorType('codeDetailLink');
            setCursorText(t('cursor_click_navigate'));
        };

        const handleLinkLeave = (e) => {
            // Don't override if it's the next project
            if (e.currentTarget.closest('.code-detail-next-project')) {
                return;
            }
            setIsHoveringLink(false);
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            // Select all links and buttons, including those with specific classes
            const links = document.querySelectorAll('a:not(.code-detail-next-project *), button:not(.code-detail-next-project *)');

            links.forEach(link => {
                link.addEventListener('mouseenter', handleLinkEnter);
                link.addEventListener('mouseleave', handleLinkLeave);
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const links = document.querySelectorAll('a:not(.code-detail-next-project *), button:not(.code-detail-next-project *)');
            links.forEach(link => {
                link.removeEventListener('mouseenter', handleLinkEnter);
                link.removeEventListener('mouseleave', handleLinkLeave);
            });
        };
    }, [location.pathname, isMobile, isCodeDetailPage, language]);

    // Add video hover detection for WorkDetail page
    useEffect(() => {
        // Skip on mobile or if not on work detail page
        if (isMobile || !isWorkDetailPage) return;

        const handleVideoEnter = () => {
            setCursorType('video');
            setCursorText(t('cursor_play_video'));
        };

        const handleVideoLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const videoElements = document.querySelectorAll('.project-video-player');

            videoElements.forEach(video => {
                video.addEventListener('mouseenter', handleVideoEnter);
                video.addEventListener('mouseleave', handleVideoLeave);
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const videoElements = document.querySelectorAll('.project-video-player');
            videoElements.forEach(video => {
                video.removeEventListener('mouseenter', handleVideoEnter);
                video.removeEventListener('mouseleave', handleVideoLeave);
            });
        };
    }, [location.pathname, isMobile, isWorkDetailPage, language]);

    // Add link hover detection for other pages
    useEffect(() => {
        // Skip link hover detection on mobile or on code pages
        if (isMobile || isCodePage || isCodeDetailPage) return;

        const handleLinkEnter = () => setIsHoveringLink(true);
        const handleLinkLeave = () => setIsHoveringLink(false);

        const links = document.querySelectorAll('a, button');

        links.forEach(link => {
            link.addEventListener('mouseenter', handleLinkEnter);
            link.addEventListener('mouseleave', handleLinkLeave);
        });

        return () => {
            links.forEach(link => {
                link.removeEventListener('mouseenter', handleLinkEnter);
                link.removeEventListener('mouseleave', handleLinkLeave);
            });
        };
    }, [location.pathname, isMobile, isCodePage, isCodeDetailPage]);

    // Add hero section hover detection for WorkDetail page
    useEffect(() => {
        // Skip on mobile or if not on work detail page
        if (isMobile || !isWorkDetailPage) return;

        const handleHeroEnter = () => {
            setCursorType('hero');
            setCursorText(t('cursor_scroll_explore'));
        };

        const handleHeroLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const heroImage = document.querySelector('.work-detail-hero-image');

            if (heroImage) {
                heroImage.addEventListener('mouseenter', handleHeroEnter);
                heroImage.addEventListener('mouseleave', handleHeroLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const heroImage = document.querySelector('.work-detail-hero-image');
            if (heroImage) {
                heroImage.removeEventListener('mouseenter', handleHeroEnter);
                heroImage.removeEventListener('mouseleave', handleHeroLeave);
            }
        };
    }, [location.pathname, isMobile, isWorkDetailPage, language]);

    // Add logo marquee hover detection for Works page
    useEffect(() => {
        // Skip on mobile or if not on works page
        if (isMobile || !isWorksPage) return;

        const handleMarqueeEnter = () => {
            setCursorType('work');
            setCursorText(t('cursor_collaboration'));
        };

        const handleMarqueeLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const marqueeSection = document.querySelector('.logo-marquee-section');

            if (marqueeSection) {
                marqueeSection.addEventListener('mouseenter', handleMarqueeEnter);
                marqueeSection.addEventListener('mouseleave', handleMarqueeLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const marqueeSection = document.querySelector('.logo-marquee-section');
            if (marqueeSection) {
                marqueeSection.removeEventListener('mouseenter', handleMarqueeEnter);
                marqueeSection.removeEventListener('mouseleave', handleMarqueeLeave);
            }
        };
    }, [location.pathname, isMobile, isWorksPage, language]);

    useEffect(() => {
        if (isMobile || location.pathname !== '/') return;

        const handleCodeShowcaseEnter = () => {
            setIsHovering(true);
        };

        const handleCodeShowcaseLeave = () => {
            setIsHovering(false);
            setCursorType('dark');
            setCursorText('');
        };

        const handleProjectCardEnter = () => {
            setCursorType('codeProject');
            setCursorText(t('cursor_show_project'));
        };

        const handleProjectCardLeave = () => {
            setCursorType('dark');
            setCursorText('');
        };

        const handleInputEnter = () => {
            setCursorType('codeProject');
            setCursorText(t('cursor_type_project'));
        };

        const handleInputLeave = () => {
            setCursorType('dark');
            setCursorText('');
        };

        const handleViewAllEnter = () => {
            setCursorType('codeProject');
            setCursorText(t('cursor_click_navigate'));
        };

        const handleViewAllLeave = () => {
            setCursorType('dark');
            setCursorText('');
        };

        const timeoutId = setTimeout(() => {
            const codeShowcaseSection = document.querySelector('.code-showcase-section');
            const projectCards = document.querySelectorAll('.code-showcase-section .group.cursor-pointer');
            const commandInput = document.querySelector('.code-showcase-section input');
            const viewAllLink = document.querySelector('.code-showcase-view-all-link');

            if (codeShowcaseSection) {
                codeShowcaseSection.addEventListener('mouseenter', handleCodeShowcaseEnter);
                codeShowcaseSection.addEventListener('mouseleave', handleCodeShowcaseLeave);
            }

            projectCards.forEach(card => {
                card.addEventListener('mouseenter', handleProjectCardEnter);
                card.addEventListener('mouseleave', handleProjectCardLeave);
            });

            if (commandInput) {
                commandInput.addEventListener('mouseenter', handleInputEnter);
                commandInput.addEventListener('mouseleave', handleInputLeave);
            }

            if (viewAllLink) {
                viewAllLink.addEventListener('mouseenter', handleViewAllEnter);
                viewAllLink.addEventListener('mouseleave', handleViewAllLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const codeShowcaseSection = document.querySelector('.code-showcase-section');
            const projectCards = document.querySelectorAll('.code-showcase-section .group.cursor-pointer');
            const commandInput = document.querySelector('.code-showcase-section input');
            const viewAllLink = document.querySelector('.code-showcase-view-all-link');

            if (codeShowcaseSection) {
                codeShowcaseSection.removeEventListener('mouseenter', handleCodeShowcaseEnter);
                codeShowcaseSection.removeEventListener('mouseleave', handleCodeShowcaseLeave);
            }

            projectCards.forEach(card => {
                card.removeEventListener('mouseenter', handleProjectCardEnter);
                card.removeEventListener('mouseleave', handleProjectCardLeave);
            });

            if (commandInput) {
                commandInput.removeEventListener('mouseenter', handleInputEnter);
                commandInput.removeEventListener('mouseleave', handleInputLeave);
            }

            if (viewAllLink) {
                viewAllLink.removeEventListener('mouseenter', handleViewAllEnter);
                viewAllLink.removeEventListener('mouseleave', handleViewAllLeave);
            }
        };
    }, [location.pathname, isMobile, language]);

    // Computers Showcase section - handle screen hover for "EXPLORE WORK" text
    useEffect(() => {
        if (isMobile || location.pathname !== '/') return;

        // When hovering on computer screens (schermo_1-5), show "EXPLORE WORK"
        if (isHoveringComputerScreen) {
            setCursorType('work');
            setCursorText(t('cursor_explore_works'));
        }
    }, [isMobile, location.pathname, isHoveringComputerScreen, language]);

    // Computers Showcase section - handle lamp hover
    useEffect(() => {
        if (isMobile || location.pathname !== '/') return;

        if (isHoveringLamp) {
            setCursorType('lamp');
            setCursorText(isLampOn ? t('cursor_turn_off_lights') : t('cursor_turn_on_lights'));
        }
    }, [isMobile, location.pathname, isHoveringLamp, isLampOn, language]);

    // Computers Showcase section - handle reel screen hover
    useEffect(() => {
        if (isMobile || location.pathname !== '/') return;

        if (isHoveringReel) {
            setCursorType('reel');
            setCursorText(t('cursor_show_reel'));
        }
    }, [isMobile, location.pathname, isHoveringReel, language]);

    // Computers Showcase section detection - hide "scroll to explore" when in this section
    useEffect(() => {
        if (isMobile || location.pathname !== '/') return;

        const handleComputersEnter = () => {
            setIsHovering(true);
            // Set default cursor for computers section (no text)
            if (!isHoveringComputerScreen) {
                setCursorType('dark');
                setCursorText('');
            }
        };

        const handleComputersLeave = () => {
            setIsHovering(false);
        };

        const timeoutId = setTimeout(() => {
            const computersSection = document.querySelector('.computers-showcase-section');

            if (computersSection) {
                computersSection.addEventListener('mouseenter', handleComputersEnter);
                computersSection.addEventListener('mouseleave', handleComputersLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const computersSection = document.querySelector('.computers-showcase-section');

            if (computersSection) {
                computersSection.removeEventListener('mouseenter', handleComputersEnter);
                computersSection.removeEventListener('mouseleave', handleComputersLeave);
            }
        };
    }, [location.pathname, isMobile, isHoveringComputerScreen]);

    // Add email and camera hover detection for Home Contact section
    useEffect(() => {
        // Skip on mobile or if not on home page
        if (isMobile || location.pathname !== '/') return;

        const handleEmailEnter = () => {
            setCursorType('contact');
            setCursorText(t('cursor_get_in_touch'));
        };

        const handleCameraEnter = () => {
            setCursorType('contact');
            setCursorText(t('cursor_activate_camera'));
        };

        const handleContactLeave = () => {
            setCursorType('contact');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const emailLink = document.querySelector('.contact-email-link');
            const camera3D = document.querySelector('.camera-3d-model');

            if (emailLink) {
                emailLink.addEventListener('mouseenter', handleEmailEnter);
                emailLink.addEventListener('mouseleave', handleContactLeave);
            }

            if (camera3D) {
                camera3D.addEventListener('mouseenter', handleCameraEnter);
                camera3D.addEventListener('mouseleave', handleContactLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const emailLink = document.querySelector('.contact-email-link');
            const camera3D = document.querySelector('.camera-3d-model');

            if (emailLink) {
                emailLink.removeEventListener('mouseenter', handleEmailEnter);
                emailLink.removeEventListener('mouseleave', handleContactLeave);
            }

            if (camera3D) {
                camera3D.removeEventListener('mouseenter', handleCameraEnter);
                camera3D.removeEventListener('mouseleave', handleContactLeave);
            }
        };
    }, [location.pathname, isMobile, language]);

    // Add social link hover detection for Home page (LinkedIn, Gumroad)
    useEffect(() => {
        // Skip on mobile or if not on home page
        if (isMobile || location.pathname !== '/') return;

        const handleSocialLinkEnter = () => {
            setCursorType('socialLink');
            setCursorText(t('cursor_click_navigate'));
        };

        const handleSocialLinkLeave = () => {
            setCursorType('hero');
            setCursorText(t('cursor_scroll_explore'));
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const socialLinks = document.querySelectorAll('.home-social-link');

            socialLinks.forEach(link => {
                link.addEventListener('mouseenter', handleSocialLinkEnter);
                link.addEventListener('mouseleave', handleSocialLinkLeave);
            });
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const socialLinks = document.querySelectorAll('.home-social-link');
            socialLinks.forEach(link => {
                link.removeEventListener('mouseenter', handleSocialLinkEnter);
                link.removeEventListener('mouseleave', handleSocialLinkLeave);
            });
        };
    }, [location.pathname, isMobile, language]);

    // Add header navigation link hover detection (all pages)
    useEffect(() => {
        // Skip on mobile or playground page (playground has its own cursor system)
        if (isMobile || isPlaygroundPage) return;

        const handleHeaderLinkEnter = () => {
            setCursorType('socialLink');
            setCursorText(t('cursor_click_navigate'));
        };

        const handleHeaderLinkLeave = () => {
            // Reset to appropriate cursor based on current page
            if (isDarkPage) {
                setCursorType('white');
                setCursorText('');
            } else if (location.pathname === '/') {
                setCursorType('hero');
                setCursorText(t('cursor_scroll_explore'));
            } else {
                setCursorType('default');
                setCursorText('');
            }
        };

        const attachListeners = () => {
            const headerLinks = document.querySelectorAll('.header-nav-link');
            headerLinks.forEach(link => {
                // Remove first to avoid duplicates
                link.removeEventListener('mouseenter', handleHeaderLinkEnter);
                link.removeEventListener('mouseleave', handleHeaderLinkLeave);
                // Add listeners
                link.addEventListener('mouseenter', handleHeaderLinkEnter);
                link.addEventListener('mouseleave', handleHeaderLinkLeave);
            });
        };

        // Initial attachment
        const timeoutId = setTimeout(attachListeners, 100);

        // Use MutationObserver to re-attach listeners when header DOM changes
        const observer = new MutationObserver(() => {
            attachListeners();
        });

        // Observe the header element for changes
        const header = document.querySelector('header');
        if (header) {
            observer.observe(header, { childList: true, subtree: true });
        }

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
            const headerLinks = document.querySelectorAll('.header-nav-link');
            headerLinks.forEach(link => {
                link.removeEventListener('mouseenter', handleHeaderLinkEnter);
                link.removeEventListener('mouseleave', handleHeaderLinkLeave);
            });
        };
    }, [location.pathname, isMobile, isPlaygroundPage, isDarkPage, language]);

    // Add logo hover detection (go back home)
    useEffect(() => {
        // Skip on mobile, playground, or home page (logo doesn't link on home)
        if (isMobile || isPlaygroundPage || location.pathname === '/') return;

        const handleLogoEnter = () => {
            setCursorType('socialLink');
            setCursorText(t('cursor_go_back_home'));
        };

        const handleLogoLeave = () => {
            // Reset to appropriate cursor based on current page
            if (isDarkPage) {
                setCursorType('white');
                setCursorText('');
            } else {
                setCursorType('default');
                setCursorText('');
            }
        };

        const attachListeners = () => {
            const logoLink = document.querySelector('.header-logo-link');
            if (logoLink) {
                logoLink.removeEventListener('mouseenter', handleLogoEnter);
                logoLink.removeEventListener('mouseleave', handleLogoLeave);
                logoLink.addEventListener('mouseenter', handleLogoEnter);
                logoLink.addEventListener('mouseleave', handleLogoLeave);
            }
        };

        // Initial attachment
        const timeoutId = setTimeout(attachListeners, 100);

        // Use MutationObserver to re-attach listeners when header DOM changes
        const observer = new MutationObserver(() => {
            attachListeners();
        });

        const header = document.querySelector('header');
        if (header) {
            observer.observe(header, { childList: true, subtree: true });
        }

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
            const logoLink = document.querySelector('.header-logo-link');
            if (logoLink) {
                logoLink.removeEventListener('mouseenter', handleLogoEnter);
                logoLink.removeEventListener('mouseleave', handleLogoLeave);
            }
        };
    }, [location.pathname, isMobile, isPlaygroundPage, isDarkPage, language]);

    // Add next project hover detection for CodeDetail page
    useEffect(() => {
        // Skip on mobile or if not on code detail page
        if (isMobile || !isCodeDetailPage) return;

        const handleNextProjectEnter = () => {
            setCursorType('codeProject');
            setCursorText(t('cursor_view_project'));
        };

        const handleNextProjectLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const nextProject = document.querySelector('.code-detail-next-project');

            if (nextProject) {
                nextProject.addEventListener('mouseenter', handleNextProjectEnter);
                nextProject.addEventListener('mouseleave', handleNextProjectLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const nextProject = document.querySelector('.code-detail-next-project');
            if (nextProject) {
                nextProject.removeEventListener('mouseenter', handleNextProjectEnter);
                nextProject.removeEventListener('mouseleave', handleNextProjectLeave);
            }
        };
    }, [location.pathname, isMobile, isCodeDetailPage, language]);

    // Add scroll-to-top button hover detection for WorkDetail and CodeDetail pages
    useEffect(() => {
        // Skip on mobile or if not on work detail or code detail page
        if (isMobile || (!isWorkDetailPage && !isCodeDetailPage)) return;

        const handleScrollTopEnter = () => {
            setCursorType('scrollTop');
            setCursorText(t('cursor_scroll_top'));
        };

        const handleScrollTopLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const scrollTopBtn = document.querySelector('.scroll-to-top-btn');

            if (scrollTopBtn) {
                scrollTopBtn.addEventListener('mouseenter', handleScrollTopEnter);
                scrollTopBtn.addEventListener('mouseleave', handleScrollTopLeave);
            }
        }, 100);

        // Use MutationObserver to detect when button appears/disappears
        const observer = new MutationObserver(() => {
            const scrollTopBtn = document.querySelector('.scroll-to-top-btn');
            if (scrollTopBtn) {
                scrollTopBtn.removeEventListener('mouseenter', handleScrollTopEnter);
                scrollTopBtn.removeEventListener('mouseleave', handleScrollTopLeave);
                scrollTopBtn.addEventListener('mouseenter', handleScrollTopEnter);
                scrollTopBtn.addEventListener('mouseleave', handleScrollTopLeave);
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
            const scrollTopBtn = document.querySelector('.scroll-to-top-btn');
            if (scrollTopBtn) {
                scrollTopBtn.removeEventListener('mouseenter', handleScrollTopEnter);
                scrollTopBtn.removeEventListener('mouseleave', handleScrollTopLeave);
            }
        };
    }, [location.pathname, isMobile, isWorkDetailPage, isCodeDetailPage, language]);

    // Add Skills Showcase 3D hover detection for About page
    useEffect(() => {
        // Skip on mobile or if not on about page
        if (isMobile || !isAboutPage) return;

        const handleSkillsEnter = () => {
            setCursorType('skills');
            setCursorText(t('cursor_everyday_tools'));
        };

        const handleSkillsLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const skillsShowcase = document.querySelector('.skills-showcase-3d');

            if (skillsShowcase) {
                skillsShowcase.addEventListener('mouseenter', handleSkillsEnter);
                skillsShowcase.addEventListener('mouseleave', handleSkillsLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const skillsShowcase = document.querySelector('.skills-showcase-3d');
            if (skillsShowcase) {
                skillsShowcase.removeEventListener('mouseenter', handleSkillsEnter);
                skillsShowcase.removeEventListener('mouseleave', handleSkillsLeave);
            }
        };
    }, [location.pathname, isMobile, isAboutPage, language]);

    // Add email hover detection for About page
    useEffect(() => {
        // Skip on mobile or if not on about page
        if (isMobile || !isAboutPage) return;

        const handleEmailEnter = () => {
            setCursorType('contact');
            setCursorText(t('cursor_get_in_touch'));
        };

        const handleEmailLeave = () => {
            setCursorType('white');
            setCursorText('');
        };

        // Use a timeout to ensure DOM elements are ready
        const timeoutId = setTimeout(() => {
            const emailLink = document.querySelector('.contact-email-link');

            if (emailLink) {
                emailLink.addEventListener('mouseenter', handleEmailEnter);
                emailLink.addEventListener('mouseleave', handleEmailLeave);
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const emailLink = document.querySelector('.contact-email-link');
            if (emailLink) {
                emailLink.removeEventListener('mouseenter', handleEmailEnter);
                emailLink.removeEventListener('mouseleave', handleEmailLeave);
            }
        };
    }, [location.pathname, isMobile, isAboutPage, language]);

    // Language flag hover detection (all pages)
    useEffect(() => {
        if (isMobile || isPlaygroundPage) return;

        const handleFlagEnter = (e) => {
            const target = e.currentTarget.getAttribute('data-lang-target');
            const text = target === 'it' ? 'Versione italiana' : 'English version';
            setCursorType('socialLink');
            setCursorText(text);
        };

        const handleFlagLeave = () => {
            if (isDarkPage) {
                setCursorType('white');
            } else if (location.pathname === '/') {
                setCursorType('hero');
                setCursorText(t('cursor_scroll_explore'));
                return;
            } else {
                setCursorType('default');
            }
            setCursorText('');
        };

        const attachListeners = () => {
            const flagBtn = document.querySelector('.language-switcher-flag');
            if (flagBtn) {
                flagBtn.removeEventListener('mouseenter', handleFlagEnter);
                flagBtn.removeEventListener('mouseleave', handleFlagLeave);
                flagBtn.addEventListener('mouseenter', handleFlagEnter);
                flagBtn.addEventListener('mouseleave', handleFlagLeave);
            }
        };

        const timeoutId = setTimeout(attachListeners, 100);

        const observer = new MutationObserver(attachListeners);
        const header = document.querySelector('header');
        if (header) observer.observe(header, { childList: true, subtree: true });

        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
            const flagBtn = document.querySelector('.language-switcher-flag');
            if (flagBtn) {
                flagBtn.removeEventListener('mouseenter', handleFlagEnter);
                flagBtn.removeEventListener('mouseleave', handleFlagLeave);
            }
        };
    }, [location.pathname, isMobile, isPlaygroundPage, isDarkPage, language]);

    // Hide default cursor
    useEffect(() => {
        // Skip cursor hiding on mobile
        if (isMobile) return;

        // On playground, hide default cursor to show custom one
        if (isPlaygroundPage) {
            document.body.style.cursor = 'none';
            const elements = document.querySelectorAll('a, button, *');
            elements.forEach(el => {
                el.style.cursor = 'none';
            });

            return () => {
                // More aggressive cleanup when leaving playground
                document.body.style.cursor = '';
                const allElements = document.querySelectorAll('*');
                allElements.forEach(el => {
                    el.style.cursor = '';
                });
            };
        }

        // For other pages, hide default cursor
        document.body.style.cursor = 'none';

        const elements = document.querySelectorAll('a, button');
        elements.forEach(el => {
            el.style.cursor = 'none';
        });

        return () => {
            document.body.style.cursor = '';
            elements.forEach(el => {
                el.style.cursor = '';
            });
        };
    }, [isMobile, isPlaygroundPage]);

    // EARLY RETURN SOLO DOPO TUTTI I HOOKS
    if (isMobile) {
        return null;
    }

    // Cursor style based on type
    const getCursorStyle = () => {
        const baseStyle = {
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999,
            pointerEvents: 'none',
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: 'transform 0.1s ease, width 0.1s ease, height 0.1s ease, background 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            mixBlendMode: 'difference',
        };

        // Base size for circular cursors
        const baseSize = isHoveringLink ? 40 : 32;

        switch (cursorType) {
            case 'hero':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'dark':
                return {
                    ...baseStyle,
                    width: `${baseSize}px`,
                    height: `${baseSize}px`,
                    background: 'white',
                    transform: `translate(${position.x - baseSize / 2}px, ${position.y - baseSize / 2}px)`,
                };
            case 'white':
                return {
                    ...baseStyle,
                    width: `${baseSize}px`,
                    height: `${baseSize}px`,
                    background: 'white',
                    transform: `translate(${position.x - baseSize / 2}px, ${position.y - baseSize / 2}px)`,
                };
            case 'work':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'contact':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'terminal':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'codeProject':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'codeDetailLink':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'playgroundLink':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'socialLink':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'skills':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'scrollTop':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'video':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'lamp':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'reel':
                return {
                    ...baseStyle,
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
            case 'linuxPointer':
                return {
                    ...baseStyle,
                    mixBlendMode: 'normal',
                    transform: `translate(${position.x}px, ${position.y}px)`,
                };
            case 'macPointer':
                return {
                    ...baseStyle,
                    mixBlendMode: 'normal',
                    transform: `translate(${position.x}px, ${position.y}px)`,
                };
            case 'windowsPointer':
                return {
                    ...baseStyle,
                    mixBlendMode: 'normal',
                    transform: `translate(${position.x}px, ${position.y}px)`,
                };
            case 'macGrab':
            case 'macHover':
            case 'macSpinner':
                return {
                    ...baseStyle,
                    mixBlendMode: 'normal',
                    transform: `translate(${position.x}px, ${position.y}px)`,
                };
            case 'linuxGrab':
            case 'linuxHover':
                return {
                    ...baseStyle,
                    mixBlendMode: 'normal',
                    transform: `translate(${position.x}px, ${position.y}px)`,
                };
            case 'windowsGrab':
            case 'windowsHover':
                return {
                    ...baseStyle,
                    mixBlendMode: 'normal',
                    transform: `translate(${position.x}px, ${position.y}px)`,
                };
            default:
                return {
                    ...baseStyle,
                    width: `${baseSize}px`,
                    height: `${baseSize}px`,
                    background: 'white',
                    transform: `translate(${position.x - 0}px, ${position.y - 0}px)`,
                };
        }
    };

    if (!visible) return null;

    return (
        <div style={getCursorStyle()} className={`custom-cursor cursor-${cursorType} ${isHoveringLink ? 'link-hover' : ''}`}>
            {/* macOS pointer for Playground */}
            {cursorType === 'macPointer' && (
                <img
                    src="/img/cursors/mac.png"
                    alt="cursor"
                    style={{
                        width: '30px',
                        height: '30px',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* Linux pointer for Playground */}
            {cursorType === 'linuxPointer' && (
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    }}
                >
                    {/* Classic Linux pointer shape */}
                    <path
                        d="M3 3 L3 20 L8 15 L11 22 L13 21 L10 14 L17 14 L3 3 Z"
                        fill="white"
                        stroke="black"
                        strokeWidth="1"
                    />
                </svg>
            )}

            {/* Windows pointer for Playground (with rotation animation) */}
            {cursorType === 'windowsPointer' && (
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    style={{
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                    }}
                    className="windows-cursor-rotating"
                >
                    {/* Windows arrow pointer shape */}
                    <path
                        d="M2 2 L2 13 L6 9 L8.5 15 L10 14.5 L7.5 8.5 L12 8.5 L2 2 Z"
                        fill="white"
                        stroke="black"
                        strokeWidth="0.5"
                    />
                </svg>
            )}

            {/* macOS grab cursor (for window dragging) */}
            {cursorType === 'macGrab' && (
                <img
                    src="/img/cursors/mac.png"
                    alt="cursor"
                    style={{
                        width: '20px',
                        height: '20px',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* macOS hover cursor (pointer hand) */}
            {cursorType === 'macHover' && (
                <img
                    src="/img/cursors/mac_click.png"
                    alt="cursor"
                    style={{
                        width: '30px',
                        height: '30px',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* macOS spinner cursor (loading) */}
            {cursorType === 'macSpinner' && (
                <img
                    src="/img/cursors/mac_spin.png"
                    alt="cursor"
                    className="mac-spinner-rotating"
                    style={{
                        width: '30px',
                        height: '30px',
                        pointerEvents: 'none',
                    }}
                />
            )}

            {/* Linux grab cursor */}
            {cursorType === 'linuxGrab' && (
                <svg
                    width="26"
                    height="26"
                    viewBox="0 0 26 26"
                    fill="none"
                    style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    }}
                >
                    <path
                        d="M14 7C14 6.4 13.6 6 13 6C12.4 6 12 6.4 12 7V13H11V8C11 7.4 10.6 7 10 7C9.4 7 9 7.4 9 8V13H8V10C8 9.4 7.6 9 7 9C6.4 9 6 9.4 6 10V16.5C6 19 8 21 10.5 21H15.5C18 21 20 19 20 16.5V12C20 11.4 19.6 11 19 11C18.4 11 18 11.4 18 12V13H17V9C17 8.4 16.6 8 16 8C15.4 8 15 8.4 15 9V13H14V7Z"
                        fill="white"
                        stroke="black"
                        strokeWidth="1"
                    />
                </svg>
            )}

            {/* Linux hover cursor */}
            {cursorType === 'linuxHover' && (
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                    }}
                >
                    <path
                        d="M11 4C11 3.4 10.6 3 10 3C9.4 3 9 3.4 9 4V10H8V6C8 5.4 7.6 5 7 5C6.4 5 6 5.4 6 6V13.5L5 13L4 14L6.5 18C7 19 8 20 9.5 20H14C16 20 18 18 18 16V10C18 9.4 17.6 9 17 9C16.4 9 16 9.4 16 10V11H15V7C15 6.4 14.6 6 14 6C13.4 6 13 6.4 13 7V11H12V5C12 4.4 11.6 4 11 4Z"
                        fill="white"
                        stroke="black"
                        strokeWidth="1"
                    />
                </svg>
            )}

            {/* Windows grab cursor */}
            {cursorType === 'windowsGrab' && (
                <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                    }}
                >
                    <path
                        d="M13 6C13 5.4 12.6 5 12 5C11.4 5 11 5.4 11 6V12H10V7C10 6.4 9.6 6 9 6C8.4 6 8 6.4 8 7V12H7V9C7 8.4 6.6 8 6 8C5.4 8 5 8.4 5 9V15.5C5 18 7 20 9.5 20H14.5C17 20 19 18 19 15.5V11C19 10.4 18.6 10 18 10C17.4 10 17 10.4 17 11V12H16V8C16 7.4 15.6 7 15 7C14.4 7 14 7.4 14 8V12H13V6Z"
                        fill="white"
                        stroke="black"
                        strokeWidth="0.5"
                    />
                </svg>
            )}

            {/* Windows hover cursor */}
            {cursorType === 'windowsHover' && (
                <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    style={{
                        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                    }}
                >
                    <path
                        d="M10 3C10 2.4 9.6 2 9 2C8.4 2 8 2.4 8 3V9H7V5C7 4.4 6.6 4 6 4C5.4 4 5 4.4 5 5V12.5L4 12L3 13L5.5 17C6 18 7 19 8.5 19H13C15 19 17 17 17 15V9C17 8.4 16.6 8 16 8C15.4 8 15 8.4 15 9V10H14V6C14 5.4 13.6 5 13 5C12.4 5 12 5.4 12 6V10H11V4C11 3.4 10.6 3 10 3Z"
                        fill="white"
                        stroke="black"
                        strokeWidth="0.5"
                    />
                </svg>
            )}

            {/* Center dot for hero, contact, terminal, work, video, lamp, reel, scrollTop, and code pages */}
            {(cursorType === 'hero' || cursorType === 'contact' || cursorType === 'terminal' || cursorType === 'work' || cursorType === 'video' || cursorType === 'lamp' || cursorType === 'reel' || cursorType === 'codeProject' || cursorType === 'codeDetailLink' || cursorType === 'playgroundLink' || cursorType === 'socialLink' || cursorType === 'skills' || cursorType === 'scrollTop') && (
                <div
                    style={{
                        width: isHoveringLink ? '30px' : '20px',
                        height: isHoveringLink ? '30px' : '20px',
                        borderRadius: '50%',
                        background: 'white',
                        position: 'absolute',
                        transition: 'width 0.2s ease, height 0.2s ease',
                    }}
                />
            )}

            {/* Rotating text for hero, contact, terminal, work, video, lamp, reel, scrollTop, and code pages */}
            {(cursorType === 'hero' || cursorType === 'contact' || cursorType === 'terminal' || cursorType === 'work' || cursorType === 'video' || cursorType === 'lamp' || cursorType === 'reel' || cursorType === 'codeProject' || cursorType === 'codeDetailLink' || cursorType === 'playgroundLink' || cursorType === 'socialLink' || cursorType === 'skills' || cursorType === 'scrollTop') && cursorText && (
                <RotatingText
                    text={cursorText}
                    color="white"
                    size={isHoveringLink ? 140 : 130}
                    glitch={cursorText === t('cursor_activate_camera')}
                />
            )}
        </div>
    );
};

export default CustomCursor;